import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig as ShadcnChartConfig,
} from '@/components/ui/chart';
import {
  Table as ShadTable,
  TableBody as ShadTableBody,
  TableCell as ShadTableCell,
  TableHead,
  TableHeader,
  TableRow as ShadTableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { IconChartBar, IconCopy, IconPlayerPlay, IconRefresh, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

interface AiSqlField {
  name: string;
  type: string;
  description?: string;
}

interface AiSqlResponse {
  // Set by the API when the model declined the request; `sql` is then empty.
  refused: boolean;
  sql: string;
  rows: Record<string, any>[];
  fields: AiSqlField[];
  totalRows: number;
  truncated: boolean;
  jobId?: string;
  location: string;
  executionMs: number;
  generatedAt: string;
  warnings?: string[];
}

interface AutoSqlChartData {
  categories: string[];
  values: number[];
  categoryLabel: string;
  valueLabel: string;
}

export function AiSqlPage() {
  const [question, setQuestion] = useState('');
  const [maxRows, setMaxRows] = useState<number>(200);
  const [result, setResult] = useState<AiSqlResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'bar' | 'pie'>('table');

  const quickPrompts = [
    {
      label: 'Income this month',
      prompt: 'Show our income for the current month split by transaction type with totals.',
    },
    {
      label: 'Income vs expenses',
      prompt: 'Compare total income (debits) and expenses (credits) for the current month.',
    },
    {
      label: 'Daily income trend',
      prompt: 'List total income by day for the last 14 days.',
    },
    {
      label: 'Top vouchers 30 days',
      prompt: 'Show the top 10 voucher types sold in the last 30 days with quantities.',
    },
  ];

  const runQuery = async (prompt: string, overrideMaxRows?: number) => {
    if (loading) return;

    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      setError('Please provide a question.');
      return;
    }

    const resolvedMaxRows = overrideMaxRows ?? maxRows;

    setError('');
    setCopied(false);
    setLoading(true);
    setResult(null);
    setViewMode('table');

    try {
      const response = await api.post('/ai-sql/query', {
        question: trimmedPrompt,
        maxRows: resolvedMaxRows,
      });

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            'Failed to generate SQL. Please try again in a moment.'
        );
      }

      setResult(response.data.data);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to run AI SQL query.';
      setError(message);

      const errorSql =
        err?.response?.data?.sql ||
        err?.sql ||
        err?.response?.data?.data?.sql ||
        '';

      if (errorSql) {
        setResult({
          refused: false,
          sql: errorSql,
          rows: [],
          fields: [],
          totalRows: 0,
          truncated: false,
          location: '',
          executionMs: 0,
          generatedAt: new Date().toISOString(),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    runQuery(question);
  };

  const handleQuickPrompt = (prompt: string) => {
    setQuestion(prompt);
    runQuery(prompt);
  };

  const handleReset = () => {
    setQuestion('');
    setResult(null);
    setError('');
    setCopied(false);
    setViewMode('table');
  };

  const handleCopySql = async () => {
    if (!result?.sql) return;
    try {
      await navigator.clipboard.writeText(result.sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Unable to copy SQL to clipboard.');
    }
  };

  const renderCellValue = (value: any) => {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const columns =
    result?.fields?.length
      ? result.fields
      : result?.rows?.length
      ? Object.keys(result.rows[0] || {}).map((key) => ({
          name: key,
          type: typeof result.rows[0][key],
        }))
      : [];

  const isActionDenied = result?.refused === true;

  const chartConfig = useMemo<AutoSqlChartData | null>(() => {
    if (!result || !result.rows || result.rows.length === 0) {
      return null;
    }

    const rows = result.rows;
    const fields = result.fields ?? [];
    const numericTypes = new Set([
      'INT64',
      'INTEGER',
      'FLOAT64',
      'FLOAT',
      'NUMERIC',
      'BIGNUMERIC',
      'DECIMAL',
      'DOUBLE',
    ]);
    const stringTypes = new Set(['STRING']);

    const normalizedFields = fields.map((field) => ({
      name: field.name,
      type: field.type?.toUpperCase?.() ?? '',
    }));

    const rowKeys = Object.keys(rows[0] || {});

    const parseNumeric = (input: any): number | null => {
      if (input === null || input === undefined) {
        return null;
      }

      if (typeof input === 'number') {
        return Number.isFinite(input) ? input : null;
      }

      const cleaned = String(input).replace(/[^\d.-]/g, '');
      if (!cleaned) {
        return null;
      }

      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const numericField =
      normalizedFields.find((field) => numericTypes.has(field.type))?.name ??
      rowKeys.find((key) =>
        rows.some((row) => {
          const parsed = parseNumeric(row[key]);
          return parsed !== null;
        }),
      );

    if (!numericField) {
      return null;
    }

    const categoryField =
      normalizedFields.find(
        (field) =>
          field.name !== numericField && stringTypes.has(field.type),
      )?.name ??
      rowKeys.find((key) => key !== numericField);

    const combined = rows
      .map((row, index) => {
        const numericValue = parseNumeric(row[numericField]);

        if (numericValue === null) {
          return null;
        }

        let categoryLabel: string;
        if (!categoryField) {
          categoryLabel = `Row ${index + 1}`;
        } else {
          const rawCategory = row[categoryField];
          categoryLabel =
            rawCategory === null || rawCategory === undefined || rawCategory === ''
              ? `Row ${index + 1}`
              : String(rawCategory);
        }

        return {
          category: categoryLabel,
          value: Number(numericValue),
        };
      })
      .filter((item): item is { category: string; value: number } => !!item);

    if (combined.length === 0) {
      return null;
    }

    const limited = combined.slice(0, 20);

    return {
      categories: limited.map((item) => item.category),
      values: limited.map((item) => item.value),
      categoryLabel: categoryField ?? 'Row',
      valueLabel: numericField,
    };
  }, [result]);

  return (
    <div>
      <PageHeader
        title="AI Data Assistant"
        subtitle="Ask questions in natural language and let Gemini generate and run read-only BigQuery SQL for you."
      />

      <form onSubmit={handleSubmit}>
        <MainCard className="mb-4">
          <div className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>
                Provide as much context as possible. The assistant only runs read-only queries and limits large result sets by default.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ai-question">What would you like to know?</Label>
              <Textarea
                id="ai-question"
                placeholder="e.g. Show the top 10 products by voucher redemptions in the last 7 days."
                rows={3}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                required
              />
            </div>

            {quickPrompts.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-muted-foreground">
                  Quick questions
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {quickPrompts.map(({ label, prompt }) => (
                    <Button
                      key={label}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickPrompt(prompt)}
                      disabled={loading}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="max-rows">Maximum Rows</Label>
              <Input
                id="max-rows"
                type="number"
                value={maxRows}
                onChange={(event) => {
                  const nextValue = Number(event.target.value);
                  setMaxRows(Number.isNaN(nextValue) ? 200 : nextValue);
                }}
                min={1}
                max={1000}
                className="max-w-40"
              />
              <span className="text-xs text-muted-foreground">
                Results will be truncated to this number of rows.
              </span>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="submit"
                size="lg"
                disabled={loading || !question.trim()}
                className="flex-1"
              >
                {loading ? (
                  <IconLoader2 className="size-5 animate-spin" />
                ) : (
                  <IconPlayerPlay className="size-5" />
                )}
                {loading ? 'Running query...' : 'Ask Gemini'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleReset}
                className="flex-1"
              >
                <IconRefresh className="size-5" />
                Reset
              </Button>
            </div>
          </div>
        </MainCard>
      </form>

      {result && (isActionDenied || result.sql) && (
        <MainCard>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="flex items-center gap-1.5 text-base font-semibold">
                <IconChartBar className="size-4" />
                {isActionDenied ? 'Request not permitted' : 'Generated SQL'}
              </h3>
              {!isActionDenied && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleCopySql}
                        className={cn(copied && 'text-green-600')}
                      />
                    }>
                      <IconCopy className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>
                      {copied ? 'Copied!' : 'Copy SQL'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {!isActionDenied && (
              <div className="rounded-lg border border-border bg-muted/50 p-3 font-mono text-sm whitespace-pre-wrap break-words">
                {result.sql}
              </div>
            )}

            {!isActionDenied && (
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary">Rows returned: {result.rows.length}</Badge>
                <Badge variant="secondary">Total rows: {result.totalRows}</Badge>
                <Badge variant="secondary">Execution: {result.executionMs} ms</Badge>
                {result.jobId && <Badge variant="secondary">Job ID: {result.jobId}</Badge>}
                <Badge variant="secondary">Location: {result.location}</Badge>
                <Badge variant="secondary">Generated: {new Date(result.generatedAt).toLocaleString()}</Badge>
                {result.truncated && (
                  <Badge variant="destructive">Results truncated</Badge>
                )}
              </div>
            )}

            {result.warnings?.map((warning, index) => (
              <Alert key={index} className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                <AlertDescription>{warning}</AlertDescription>
              </Alert>
            ))}
          </div>
        </MainCard>
      )}

      {result && (
        <MainCard className="mt-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <h3 className="text-base font-semibold">Result View</h3>
              <div className="inline-flex rounded-lg border border-border">
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-r-none"
                  onClick={() => setViewMode('table')}
                >
                  Table
                </Button>
                <Button
                  variant={viewMode === 'bar' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-none border-x border-border"
                  disabled={!chartConfig}
                  onClick={() => setViewMode('bar')}
                >
                  Bar
                </Button>
                <Button
                  variant={viewMode === 'pie' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-l-none"
                  disabled={!chartConfig}
                  onClick={() => setViewMode('pie')}
                >
                  Pie
                </Button>
              </div>
            </div>

            {isActionDenied ? (
              <Alert>
                <AlertDescription>
                  The AI assistant was unable to generate a safe query for this request.
                </AlertDescription>
              </Alert>
            ) : viewMode === 'table' ? (
              result.rows.length === 0 ? (
                <Alert>
                  <AlertDescription>No results were returned for this query.</AlertDescription>
                </Alert>
              ) : (
                <div className="overflow-auto">
                  <ShadTable>
                    <TableHeader>
                      <ShadTableRow>
                        {columns.map((column) => (
                          <TableHead key={column.name}>
                            <span className="text-sm font-medium">{column.name}</span>
                            {column.type && (
                              <span className="block text-xs text-muted-foreground">
                                {column.type}
                              </span>
                            )}
                            {'description' in column && column.description && (
                              <span className="block text-xs text-muted-foreground">
                                {column.description}
                              </span>
                            )}
                          </TableHead>
                        ))}
                      </ShadTableRow>
                    </TableHeader>
                    <ShadTableBody>
                      {result.rows.map((row, rowIndex) => (
                        <ShadTableRow key={rowIndex} className="hover:bg-muted/50">
                          {columns.map((column) => (
                            <ShadTableCell key={column.name}>
                              {renderCellValue(row[column.name])}
                            </ShadTableCell>
                          ))}
                        </ShadTableRow>
                      ))}
                    </ShadTableBody>
                  </ShadTable>
                </div>
              )
            ) : chartConfig ? (
              viewMode === 'bar' ? (
                (() => {
                  const barChartConfig: ShadcnChartConfig = {
                    value: { label: chartConfig.valueLabel, color: 'hsl(221.2 83.2% 53.3%)' },
                  };
                  const barData = chartConfig.categories.map((category, index) => ({
                    category,
                    value: chartConfig.values[index],
                  }));
                  const longCategories = chartConfig.categories.some((c) => c.length > 8);
                  return (
                    <ChartContainer config={barChartConfig} className="h-[360px] w-full">
                      <BarChart data={barData} margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="category"
                          tick={{ fontSize: 11 }}
                          interval={0}
                          angle={longCategories ? -45 : 0}
                          textAnchor={longCategories ? 'end' : 'middle'}
                          height={longCategories ? 80 : 48}
                          label={{
                            value: chartConfig.categoryLabel,
                            position: 'insideBottom',
                            offset: longCategories ? -48 : -8,
                          }}
                        />
                        <YAxis
                          label={{
                            value: chartConfig.valueLabel,
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ChartContainer>
                  );
                })()
              ) : (
                (() => {
                  const pieData = chartConfig.categories.map((category, index) => ({
                    name: category,
                    value: chartConfig.values[index],
                  }));
                  const piePalette = chartConfig.categories.map(
                    (_, index) => `hsl(${(index * 360) / Math.max(chartConfig.categories.length, 1)} 70% 50%)`,
                  );
                  const pieChartConfig: ShadcnChartConfig = {
                    value: { label: chartConfig.valueLabel },
                  };
                  return (
                    <ChartContainer config={pieChartConfig} className="h-[360px] w-full">
                      <PieChart margin={{ top: 20, bottom: 20 }}>
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          label
                        >
                          {pieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={piePalette[index]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ChartContainer>
                  );
                })()
              )
            ) : (
              <Alert>
                <AlertDescription>
                  Not enough structured numeric data to build a chart. The assistant will try to summarise the
                  results in a table instead.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </MainCard>
      )}
    </div>
  );
}
